import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { CLASS_STATUS_FILTERS } from '@/constants/classStatus';

export interface InstructorClass {
  id: string;
  type: string;
  modality: string;
  start_time: string;
  end_time: string;
  status: string;
  academy_id?: string;
  academy_name?: string;
  // Add more fields as needed
}

export interface UseInstructorClassesOptions {
  fetchFuture?: boolean; // If true, fetch classes from today onwards (not just selected date)
  limit?: number; // Limit the number of classes returned
}

export const useInstructorClasses = (
  selectedDate?: Date, 
  instructorId?: string,
  options?: UseInstructorClassesOptions
) => {
  const { user } = useAuth();

  // Determine which ID to use: instructorId if provided, otherwise user.id
  const targetUserId = instructorId || user?.id;
  const fetchFuture = options?.fetchFuture ?? false;
  const limit = options?.limit;

  const { data: classes, isLoading, error, refetch } = useQuery({
    queryKey: [
      'instructor-classes', 
      targetUserId, 
      fetchFuture ? 'future' : format(selectedDate || new Date(), 'yyyy-MM-dd'),
      limit
    ],
    queryFn: async () => {
      if (!targetUserId) return [];

      let queryStartDate: Date;
      
      if (fetchFuture) {
        // Fetch classes from NOW onwards (for home page slider)
        // This ensures we don't show classes that have already passed
        const now = new Date();
        queryStartDate = now; // Use current time, not midnight
      } else {
        // Fetch classes for the selected date only (for calendar/day view)
        const date = selectedDate || new Date();
        queryStartDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
      }
      
      // Build query based on whether instructorId was provided
      // IMPORTANT: For instructor classes, we ALWAYS search by instructor_id, not user_id
      // Classes have:
      // - user_id = student who requested the class
      // - instructor_id = instructor teaching the class
      // So we need to search by instructor_id to find classes the instructor is teaching
      // CRITICAL: Only fetch classes (is_class = true), NEVER trainings
      // Show ALL classes (Scheduled, Completed, Missed) in the list
      // Note: The slider count (get_instructor_daily_totals) shows only Completed classes
      // This allows users to see all their classes in the list, but the slider counts only completed ones
      let query = supabase
        .from('trainings')
        .select('*')
        .eq('is_class', true) // STRICT: Only classes, never trainings
        .gte('start_time', queryStartDate.toISOString())
        .in('status', CLASS_STATUS_FILTERS.LIST_VIEW); // Use centralized status filter

      // If fetching for a specific date (not future), add end date filter
      // Use DATE() function in the query to match the same logic as get_instructor_daily_totals
      if (!fetchFuture) {
        const date = selectedDate || new Date();
        const dateStr = format(date, 'yyyy-MM-dd');
        // Use DATE() function to extract date from start_time, matching get_instructor_daily_totals logic
        query = query.filter('start_time', 'lte', `${dateStr}T23:59:59.999Z`);
        // Also ensure we're getting classes for the exact date using DATE() comparison
        // We'll filter in JavaScript after fetching to ensure timezone consistency
      }

      // Always search by instructor_id for classes
      // If instructorId is provided, use it; otherwise use targetUserId as the instructor
      const instructorIdToUse = instructorId || targetUserId;
      query = query.eq('instructor_id', instructorIdToUse);

      // Apply limit if provided
      if (limit) {
        query = query.limit(limit);
      }

      const { data, error: queryError } = await query.order('start_time', { ascending: true });

      if (queryError) {
        console.error('❌ Error fetching instructor classes:', queryError);
        return [];
      }
      
      let filteredData = data || [];
      if (!fetchFuture && selectedDate) {
        const targetDateStr = format(selectedDate, 'yyyy-MM-dd');
        filteredData = filteredData.filter(classItem => {
          const classDate = new Date(classItem.start_time);
          const classDateStr = format(classDate, 'yyyy-MM-dd');
          return classDateStr === targetDateStr;
        });
      }
      
      // For future classes, filter out any that have already passed (defensive check)
      if (fetchFuture) {
        const now = new Date();
        filteredData = filteredData.filter(classItem => {
          const classStartTime = new Date(classItem.start_time);
          return classStartTime > now; // Only show classes that haven't started yet
        });
      }
      
      return filteredData;
    },
    enabled: !!targetUserId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    classes: classes || [],
    isLoading,
    error,
    refetch,
  };
};
